<script setup>
import Multiselect from "vue-multiselect";
import {trackerTypes} from "@/constants/trackers";
import {computed, inject, onMounted, ref, watch} from "vue";
import Draggable from "vuedraggable";
import customFieldsService from "@/services/custom-fields.service";
import CustomFieldEdit from "@/components/customFields/edit/CustomFieldEdit.vue";
import CustomFieldsQuestions from "@/components/trackers/trackerEdit/CustomFieldsQuestions.vue";
import {useStore} from "vue2-helpers/vuex";
import {triggerUpsell} from "@/composables/useStoreActions";

const store = useStore();

const tracker = inject('tracker');
const canEditInfo = inject('canEditInfo');

const showEditModal = ref(false);
const reviewerFieldsList = ref([]);
const diligenceFieldsList = ref([]);
const currentFieldIndex = ref(null);
const addReviewModal = ref(null);
const addReviewAggregateModal = ref(null);
const enableAggregatedFields = ref(false);
const aggregateFieldsErrors = ref([]);

const customFieldsCount = computed(() => tracker.questions.length + tracker.reviewerFields.length + tracker.reviewerAggregateFields.length + tracker.diligenceFields.length);
const editCustomFieldsLabel = computed(() => customFieldsCount.value ? canEditQuestions.value || canEditReviewFields.value || canEditInfo.value ? "Edit" : "View" : "+ Add");

const canEditQuestions = computed(() =>
    tracker.type === trackerTypes.PRIVATE
        ? tracker.userPermissions.includes('tracker-questions-edit-private')
        : tracker.userPermissions.includes('tracker-questions-edit-public')
);

const canEditReviewFields = computed(() => tracker.userPermissions.includes('tracker-reviewerFields-edit'));
const canEditCustomFields = computed(() => canEditQuestions.value || canEditReviewFields.value || canEditInfo.value);
const actualDiligenceFields = computed(() => diligenceFieldsList.value.filter(item => !tracker.diligenceFields.some(field => field && field.id === item.id)));
const actualReviewerFieldsList = computed(() => reviewerFieldsList.value.filter(item => !tracker.reviewerFields.some(field => field.field && field.field.id === item.id) && !tracker.reviewerAggregateFields.some(field => field.field && field.field.id === item.id)));

const actualReviewerAggregatedFieldsList = computed(() => actualReviewerFieldsList.value.filter(item =>
    (
        item.type === 'numeric' &&
        (
            !tracker.reviewerAggregateFields[0].field ||
            (
                item.max_value === reviewerFieldsList.value.find(field => tracker.reviewerAggregateFields[0].field?.id === field.id).max_value
                && item.min_value === reviewerFieldsList.value.find(field => tracker.reviewerAggregateFields[0].field?.id === field.id).min_value
            )
        )
    ) || item.id === -1
));

const prevReviewerFields = ref(JSON.parse(JSON.stringify(tracker.reviewerFields)));

watch(() => JSON.parse(JSON.stringify(tracker.reviewerFields)), newValue => {
    newValue.forEach((item, index) => {
        if (item.field && item.field.id === -1) {
            if (store.getters.onlyNeeds) {
                triggerUpsell(store);
                tracker.reviewerFields[index].field = prevReviewerFields.value[index]?.field ?? null;
            } else {
                currentFieldIndex.value = index;
                tracker.reviewerFields[index].field = null;
                addReviewModal.value.open(0);
            }
        }
    });

    prevReviewerFields.value = newValue;
});

const prevReviewerAggregateFields = ref(JSON.parse(JSON.stringify(tracker.reviewerAggregateFields)));

watch(() => JSON.parse(JSON.stringify(tracker.reviewerAggregateFields)), newValue => {
    newValue.forEach((item, index) => {
        if (item.field && item.field.id === -1) {
            if (store.getters.onlyNeeds) {
                triggerUpsell(store);
                tracker.reviewerAggregateFields[index].field = prevReviewerAggregateFields.value[index]?.field ?? null;
            } else {
                currentFieldIndex.value = index;
                tracker.reviewerAggregateFields[index].field = null;
                addReviewAggregateModal.value.open(0);
            }
        }
    });

    prevReviewerAggregateFields.value = newValue;
});

watch(() => tracker.reviewerAggregateFields, value => {
    enableAggregatedFields.value = !!value.length
});

onMounted(() => {
    getFieldsList();
});

async function getFieldsList() {
    getReviewsList();
    getDiligenceFields();
}

async function getReviewsList() {
    const {data} = await customFieldsService.getFieldsArray('tracker_review');
    reviewerFieldsList.value = data;

    reviewerFieldsList.value.push({
        id: -1,
        name: "Create New Field",
    });
}

async function getDiligenceFields() {
    const {data} = await customFieldsService.getFieldsArray('tracker_diligence');
    diligenceFieldsList.value = data;
}

async function updateReview(id) {
    await getReviewsList();
    tracker.reviewerFields[currentFieldIndex.value].field = reviewerFieldsList.value.find(item => item.id === id);
}

async function updateAggregateReview(id) {
    await getReviewsList();
    tracker.reviewerAggregateFields[currentFieldIndex.value].field = actualReviewerAggregatedFieldsList.value.find(item => item.id === id);
}

function validateFields(e) {
    aggregateFieldsErrors.value = [];
    let hasErrors = false;

    for (let i in tracker.reviewerAggregateFields) {
        if (tracker.reviewerAggregateFields[i].weight > 100) {
            aggregateFieldsErrors.value[i] = 'Weight cannot be more than 100%';
            hasErrors = true;
        } else if (tracker.reviewerAggregateFields[i].weight < 0) {
            aggregateFieldsErrors.value[i] = 'Weight cannot be less than 0%';
            hasErrors = true;
        }
    }

    if (hasErrors) {
        e.preventDefault();
    }
}
</script>

<template>
    <div v-if="tracker.type !== trackerTypes.ECOSYSTEMS && (customFieldsCount || canEditCustomFields)" class="d-flex align-items-center mb-4">

        <h5 class="heading5 heading5--secondary mr-4 mb-0">Custom Fields</h5>
        <template v-if="customFieldsCount"><span class="fw-bold mr-2">{{customFieldsCount}} Added</span></template>

        <div class="link-wrapper m-0 p-0">
            <a
                class="link m-0"
                :class="{'link-edit link-wrapper__edit': customFieldsCount && canEditCustomFields}"
                @click="showEditModal = true"
            >
                {{editCustomFieldsLabel}}
            </a>
        </div>

        <b-modal v-model="showEditModal" title="Customizations" @hide="validateFields">
            <template v-if="canEditQuestions">
                <p v-if="tracker.type === trackerTypes.PUBLIC" class="modal-text">
                    Add fields startups and reviewers fill out in order to apply tracker / evaluate startups.
                </p>

                <p v-else class="modal-text">
                    Add fields reviewers fill out in order to better evaluate startups.
                </p>
            </template>

            <template v-if="tracker.type === trackerTypes.PUBLIC && (canEditQuestions || tracker.questions.length)">
                <h5 class="heading5 heading5--secondary">Questions</h5>
                <p class="modal-text tracker-hint">Fields startups will need to fill out when applying to this tracker</p>

                <CustomFieldsQuestions
                    :can-edit-questions="canEditQuestions"
                />
            </template>

            <template v-if="tracker.reviewsEnabled && (canEditReviewFields || tracker.reviewerFields.length)">
                <h5 class="heading5 heading5--secondary">Reviewer Fields</h5>
                <p class="modal-text tracker-hint">Fields your team members will fill out in order to review startups</p>

                <Draggable v-model="tracker.reviewerFields" class="list-drag-n-drop--full list-drag-n-drop--complex" :class="{'list-drag-n-drop': canEditReviewFields}" handle=".draggable">
                    <div v-for="(field, index) in tracker.reviewerFields" :key="'r'+index" class="w-100" :class="{draggable: canEditReviewFields}">

                        <div class="mb-3 pr-0" :class="{'list-drag-n-drop__item': canEditReviewFields}">
                            <span class="p-0"></span>

                            <Multiselect
                                v-model="tracker.reviewerFields[index].field"
                                class="multiselect-mid"
                                :options="actualReviewerFieldsList"
                                :disabled="!canEditReviewFields"
                                select-label=""
                                deselect-label=""
                                label="name"
                                track-by="name"
                            />

                            <div v-if="canEditReviewFields" class="text-right">
                                <a @click="tracker.reviewerFields.splice(index, 1)" class="link-remove link-remove--round"></a>
                            </div>
                        </div>

                        <div v-if="canEditReviewFields" class="d-flex align-items-center justify-content-between mb-4">
                            <div class="input-checkbox input-checkbox--mandatory">
                                <input :id="'review-mandatory-'+index" type="checkbox" v-model="tracker.reviewerFields[index].required">
                                <label :for="'review-mandatory-'+index">Make mandatory</label>
                            </div>
                        </div>
                    </div>
                </Draggable>

                <div class="link-wrapper" v-if="canEditReviewFields">
                    <a @click="tracker.reviewerFields.push({field: null, required: 0})" class="link">+ Add Field</a>
                </div>

                <h5 class="heading5 heading5--secondary">Aggregate Rating</h5>
                <p class="modal-text tracker-hint">Replace the star rating with an aggregate of your selected fields</p>

                <div v-if="!enableAggregatedFields" class="input-checkbox mb-3">
                    <a class="link" @click="enableAggregatedFields = true">Enable</a>
                </div>

                <template v-else>
                    <Draggable v-model="tracker.reviewerAggregateFields" class="list-drag-n-drop--full list-drag-n-drop--complex list-drag-n-drop--complex--rating" :class="{'list-drag-n-drop': canEditReviewFields}" handle=".draggable">
                        <div v-for="(field, index) in tracker.reviewerAggregateFields" :key="'ra'+index" class="w-100" :class="{draggable: canEditReviewFields}">
                            <div
                                v-if="index in aggregateFieldsErrors"
                                class="form__error error mb-2"
                            >
                                {{aggregateFieldsErrors[index]}}
                            </div>

                            <div class="mb-3 pr-0" :class="{'list-drag-n-drop__item': canEditReviewFields}">
                                <span class="p-0"></span>

                                <Multiselect
                                    v-model="tracker.reviewerAggregateFields[index].field"
                                    class="multiselect-mid"
                                    :options="actualReviewerAggregatedFieldsList"
                                    :disabled="!canEditReviewFields"
                                    select-label=""
                                    deselect-label=""
                                    label="name"
                                    track-by="name"
                                />

                                <div class="like-form-control ml-2">
                                    <div class="like-form-control__type">Weight:</div>
                                    <input
                                        v-model="tracker.reviewerAggregateFields[index].weight"
                                        :disabled="!canEditReviewFields"
                                        type="number"
                                        min="0"
                                        max="100"
                                        class="form-control form-control--mid"
                                    >
                                    <div class="like-form-control__percent">%</div>
                                </div>

                                <div v-if="canEditReviewFields" class="text-right">
                                    <a @click="tracker.reviewerAggregateFields.splice(index, 1)" class="link-remove link-remove--round"></a>
                                </div>
                            </div>

                            <div v-if="canEditReviewFields" class="d-flex align-items-center justify-content-between mb-4">
                                <div class="input-checkbox input-checkbox--mandatory">
                                    <input :id="'review-aggregate-mandatory-'+index" type="checkbox" v-model="tracker.reviewerAggregateFields[index].required">
                                    <label :for="'review-aggregate-mandatory-'+index">Make mandatory</label>
                                </div>
                            </div>
                        </div>
                    </Draggable>

                    <div class="link-wrapper" v-if="canEditReviewFields">
                        <a @click="tracker.reviewerAggregateFields.push({field: null, weight: 50, required: 0})" class="link">+ Add Field</a>
                    </div>
                </template>
            </template>

            <h5 class="heading5 heading5--secondary">Startup Diligence Fields</h5>
            <p class="modal-text tracker-hint">Fields that can be used as columns in your tracker that can be filled in by users</p>

            <div v-for="(field, index) in tracker.diligenceFields" :key="'d'+index">
                <multiselect class="multiselect-mid mb-2"
                             v-model="tracker.diligenceFields[index]"
                             :options="actualDiligenceFields"
                             :disabled="!canEditInfo"
                             select-label=""
                             deselect-label=""
                             label="name"
                             track-by="name"
                >
                </multiselect>
                <div class="text-right mb-2" v-if="canEditInfo">
                    <a @click="tracker.diligenceFields.splice(index, 1)" class="link link-normal">Remove</a>
                </div>
            </div>

            <div class="link-wrapper" v-if="canEditInfo">
                <a @click="tracker.diligenceFields.push(null)" class="link">+ Add Field</a>
            </div>

            <template v-if="tracker.type === trackerTypes.PRIVATE && $store.getters.isPremiumEnterprise && (canEditQuestions || tracker.questions.length)">
                <h5 class="heading5 heading5--secondary">Startup Questions</h5>
                <p class="modal-text tracker-hint">Fields startups will be prompted to fill out in order to be evaluated.<br>
                    When startup questions are present, adding a startup to your tracker will send them a form with your fields to fill out.</p>

                <CustomFieldsQuestions
                    :can-edit-questions="canEditQuestions"
                />
            </template>

            <template #modal-footer>
                <b-button size="lg" variant="primary" @click="showEditModal = false">
                    Close
                </b-button>
            </template>
        </b-modal>

        <CustomFieldEdit
            ref="addReviewModal"
            type="tracker_review"
            @saved="updateReview"
        />

        <CustomFieldEdit
            ref="addReviewAggregateModal"
            type="tracker_review"
            fieldType="numeric"
            @saved="updateAggregateReview"
        />
    </div>
</template>

<style scoped>

</style>
